import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import './PaymentSettings.css';

function PaymentSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [creatingPlans, setCreatingPlans] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [plansResult, setPlansResult] = useState(null);
  const [paypalPlans, setPaypalPlans] = useState([]);
  const [formData, setFormData] = useState({
    paypal_enabled: false,
    paypal_client_id: '',
    paypal_secret: '',
    paypal_mode: 'sandbox',
    bank_transfer_enabled: false,
    bank_account_iban: '',
    bank_account_bic: '',
    bank_account_holder: '',
    bank_name: ''
  });

  useEffect(() => {
    fetchSettings();
    fetchPayPalPlans();
  }, []);

  const fetchPayPalPlans = async () => {
    try {
      const data = await apiClient.getPayPalPlans();
      setPaypalPlans(data);
    } catch (error) {
      console.error('Error fetching PayPal plans:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const data = await apiClient.getPaymentSettings();
      setSettings(data);
      setFormData({
        paypal_enabled: data.paypal_enabled || false,
        paypal_client_id: data.paypal_client_id || '',
        paypal_secret: '', // Never show secret
        paypal_mode: data.paypal_mode || 'sandbox',
        bank_transfer_enabled: data.bank_transfer_enabled || false,
        bank_account_iban: data.bank_account_iban || '',
        bank_account_bic: data.bank_account_bic || '',
        bank_account_holder: data.bank_account_holder || '',
        bank_name: data.bank_name || ''
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      // Only send fields that have been changed
      const updateData = {};
      Object.keys(formData).forEach(key => {
        if (key === 'paypal_secret' && !formData[key]) {
          // Don't send empty secret
          return;
        }
        updateData[key] = formData[key];
      });

      await apiClient.updatePaymentSettings(updateData);
      alert('Einstellungen erfolgreich gespeichert!');
      fetchSettings(); // Reload
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTestPayPal = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await apiClient.testPayPalConnection();
      setTestResult(result);
    } catch (error) {
      console.error('Error testing PayPal:', error);
      setTestResult({
        status: 'error',
        message: error.response?.data?.detail || error.message
      });
    } finally {
      setTesting(false);
    }
  };

  const handleCreatePlans = async () => {
    setCreatingPlans(true);
    setPlansResult(null);
    try {
      const result = await apiClient.createPayPalPlans();
      setPlansResult(result);
      fetchPayPalPlans(); // Reload plans
    } catch (error) {
      console.error('Error creating plans:', error);
      setPlansResult({
        status: 'error',
        message: error.response?.data?.detail || error.message
      });
    } finally {
      setCreatingPlans(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-settings-loading">
        <div className="spinner"></div>
        <p>Lade Einstellungen...</p>
      </div>
    );
  }

  return (
    <div className="payment-settings" data-testid="payment-settings">
      <div className="settings-header">
        <h1>Zahlungseinstellungen</h1>
        <p className="settings-subtitle">Konfiguriere PayPal und Banküberweisung</p>
      </div>

      {/* PayPal Settings */}
      <div className="settings-section">
        <div className="section-header">
          <h2>💳 PayPal Integration</h2>
          <label className="switch">
            <input
              type="checkbox"
              name="paypal_enabled"
              checked={formData.paypal_enabled}
              onChange={handleInputChange}
              data-testid="paypal-enabled-toggle"
            />
            <span className="slider"></span>
          </label>
        </div>

        {formData.paypal_enabled && (
          <div className="settings-form">
            <div className="form-group">
              <label>Modus</label>
              <select
                name="paypal_mode"
                value={formData.paypal_mode}
                onChange={handleInputChange}
                data-testid="paypal-mode-select"
              >
                <option value="sandbox">Sandbox (Test)</option>
                <option value="live">Live (Produktion)</option>
              </select>
              <small>Verwende Sandbox für Tests, Live für echte Zahlungen</small>
            </div>

            <div className="form-group">
              <label>Client ID *</label>
              <input
                type="text"
                name="paypal_client_id"
                value={formData.paypal_client_id}
                onChange={handleInputChange}
                placeholder="AYSq3RDGsmBLJE-otTkBtM-jBc..."
                data-testid="paypal-client-id-input"
              />
              <small>
                Zu finden in deinem{' '}
                <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noopener noreferrer">
                  PayPal Developer Dashboard
                </a>
              </small>
            </div>

            <div className="form-group">
              <label>Secret *</label>
              <input
                type="password"
                name="paypal_secret"
                value={formData.paypal_secret}
                onChange={handleInputChange}
                placeholder="EHaHNhPFscw-7dI7RUxs..."
                data-testid="paypal-secret-input"
              />
              <small>Wird verschlüsselt gespeichert. Leer lassen, um nicht zu ändern.</small>
            </div>

            <div className="form-actions">
              <button
                onClick={handleTestPayPal}
                disabled={testing || !formData.paypal_client_id}
                className="btn-test"
                data-testid="test-paypal-button"
              >
                {testing ? 'Teste...' : '🔍 Verbindung testen'}
              </button>
            </div>

            {testResult && (
              <div className={`test-result ${testResult.status}`} data-testid="test-result">
                <strong>{testResult.status === 'success' ? '✅' : '❌'}</strong>
                {testResult.message}
              </div>
            )}

            {/* PayPal Subscription Plans */}
            <div className="plans-section">
              <h3>📋 Subscription Plans</h3>
              <p className="plans-info">
                Für wiederkehrende Zahlungen (Abos) müssen PayPal Subscription Plans erstellt werden.
              </p>
              
              {paypalPlans.length === 0 ? (
                <div className="no-plans">
                  <p>⚠️ Keine PayPal Plans vorhanden</p>
                  <button
                    onClick={handleCreatePlans}
                    disabled={creatingPlans || !formData.paypal_client_id}
                    className="btn-create-plans"
                    data-testid="create-plans-button"
                  >
                    {creatingPlans ? 'Erstelle Plans...' : '🏗️ PayPal Plans erstellen'}
                  </button>
                </div>
              ) : (
                <div className="plans-list">
                  <p className="plans-count">✅ {paypalPlans.length} Plans erstellt</p>
                  {paypalPlans.map((plan, idx) => (
                    <div key={idx} className="plan-item">
                      <span className="plan-name">{plan.plan_name}</span>
                      <span className="plan-amount">{plan.amount}€</span>
                      <span className="plan-id">{plan.plan_id.substring(0, 20)}...</span>
                    </div>
                  ))}
                  <button
                    onClick={handleCreatePlans}
                    disabled={creatingPlans}
                    className="btn-recreate-plans"
                    data-testid="recreate-plans-button"
                  >
                    {creatingPlans ? 'Erstelle Plans...' : '🔄 Plans neu erstellen'}
                  </button>
                </div>
              )}

              {plansResult && (
                <div className={`test-result ${plansResult.plans ? 'success' : 'error'}`}>
                  <strong>{plansResult.plans ? '✅' : '❌'}</strong>
                  {plansResult.message || 'Plans erstellt'}
                  {plansResult.plans && (
                    <div className="plans-created-list">
                      {plansResult.plans.map((p, i) => (
                        <div key={i}>• {p.name}: {p.status}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bank Transfer Settings */}
      <div className="settings-section">
        <div className="section-header">
          <h2>🏦 Banküberweisung</h2>
          <label className="switch">
            <input
              type="checkbox"
              name="bank_transfer_enabled"
              checked={formData.bank_transfer_enabled}
              onChange={handleInputChange}
              data-testid="bank-transfer-enabled-toggle"
            />
            <span className="slider"></span>
          </label>
        </div>

        {formData.bank_transfer_enabled && (
          <div className="settings-form">
            <div className="form-group">
              <label>Kontoinhaber *</label>
              <input
                type="text"
                name="bank_account_holder"
                value={formData.bank_account_holder}
                onChange={handleInputChange}
                placeholder="ZENTA Frequenz GmbH"
                data-testid="bank-holder-input"
              />
            </div>

            <div className="form-group">
              <label>IBAN *</label>
              <input
                type="text"
                name="bank_account_iban"
                value={formData.bank_account_iban}
                onChange={handleInputChange}
                placeholder="DE89 3704 0044 0532 0130 00"
                data-testid="bank-iban-input"
              />
            </div>

            <div className="form-group">
              <label>BIC</label>
              <input
                type="text"
                name="bank_account_bic"
                value={formData.bank_account_bic}
                onChange={handleInputChange}
                placeholder="COBADEFFXXX"
                data-testid="bank-bic-input"
              />
            </div>

            <div className="form-group">
              <label>Bank</label>
              <input
                type="text"
                name="bank_name"
                value={formData.bank_name}
                onChange={handleInputChange}
                placeholder="Commerzbank"
                data-testid="bank-name-input"
              />
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="settings-footer">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-save"
          data-testid="save-settings-button"
        >
          {saving ? 'Speichere...' : '💾 Einstellungen speichern'}
        </button>
      </div>
    </div>
  );
}

export default PaymentSettings;